import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export function Section({ children, className = "", id, testid }) {
  return (
    <section id={id} data-testid={testid} className={`py-20 md:py-28 ${className}`}>
      <div className="mx-auto max-w-7xl px-6 md:px-10">{children}</div>
    </section>
  );
}

export function Eyebrow({ children, dark = false }) {
  return (
    <span className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] ${dark ? "border border-white/15 bg-white/[0.06] text-sky-300" : "border border-crimson/15 bg-crimson-soft text-crimson"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dark ? "bg-sky-400" : "bg-crimson"}`} />
      {children}
    </span>
  );
}

// Sharp-edged clinical button with a trailing arrow
export function PillButton({ to, href, children, variant = "navy", arrow = true, className = "", ...props }) {
  const variants = {
    navy: "bg-navy text-white hover:bg-navy-light",
    lime: "bg-crimson text-white hover:bg-crimson-dark",
    crimson: "bg-crimson text-white hover:bg-crimson-dark",
    outline: "border border-navy/20 bg-white text-navy hover:bg-secondary",
  };
  const circle = variant === "outline" ? "bg-navy/10" : "bg-white/20";
  const inner = (
    <>
      <span>{children}</span>
      {arrow && (
        <span className={`grid h-6 w-6 place-items-center rounded-md transition-transform duration-200 group-hover:scale-110 ${circle}`}>
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
        </span>
      )}
    </>
  );
  const cls = `group inline-flex items-center gap-2.5 rounded-lg pl-6 pr-3 py-3 text-sm font-semibold transition-[transform,box-shadow,background-color] duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(15,23,42,0.18)] active:translate-y-0 active:scale-[0.98] ${variants[variant]} ${className}`;
  if (href) return <a href={href} className={cls} {...props}>{inner}</a>;
  if (to) return <Link to={to} className={cls} {...props}>{inner}</Link>;
  return <button className={cls} {...props}>{inner}</button>;
}

export function SectionHeading({ eyebrow, title, subtitle, center = false, light = false }) {
  return (
    <div className={`${center ? "mx-auto max-w-2xl text-center" : "max-w-2xl"}`}>
      {eyebrow && <Eyebrow dark={light}>{eyebrow}</Eyebrow>}
      <h2 className={`mt-5 font-heading text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl ${light ? "text-white" : "text-navy"}`}>{title}</h2>
      {subtitle && <p className={`mt-5 text-base leading-relaxed sm:text-lg ${light ? "text-white/70" : "text-navy/60"}`}>{subtitle}</p>}
    </div>
  );
}

export function CheckItem({ children, light = false }) {
  return (
    <div className="flex items-center gap-3">
      <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-md ${light ? "bg-crimson" : "bg-navy"}`}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6.2 5 8.5 9.5 3.5" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </span>
      <span className={`text-sm font-medium ${light ? "text-white/85" : "text-navy/80"}`}>{children}</span>
    </div>
  );
}
